import {CognitoUser, AuthenticationDetails, CognitoRefreshToken } from "amazon-cognito-identity-js";
import { jwtDecode } from 'jwt-decode';
import userPool from './amazon-cognito-identity';
import { setSecureTokens, getSecureToken, clearSecureTokens, isAuthenticated } from './utils/secureTokenStorage';

interface AuthResult {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  }



export const login = (username: string, password: string) => {
    return new Promise((resolve, reject) => {
        const authDetails = new AuthenticationDetails({
            Username: username,
            Password: password
        });

        const user = new CognitoUser({
            Username: username,
            Pool: userPool
        });

        user.authenticateUser(authDetails, {
            onSuccess: (result) => {
                console.log("Login erfolgreich!");
                const accessToken = result.getAccessToken().getJwtToken();
                const idToken = result.getIdToken().getJwtToken();
                const refreshToken = result.getRefreshToken().getToken();

                // Use secure token storage instead of sessionStorage
                setSecureTokens({
                    accessToken,
                    idToken,
                    refreshToken
                });

                resolve({ accessToken, idToken, refreshToken });
            },
            onFailure: (err) => {
                console.error("Fehler beim Login:", err);
                reject(err);
            }
        });
    });
};


const refreshTokens = () => {
    return new Promise((resolve, reject) => {
        const refreshTokenString = getSecureToken("refreshToken");

        if (!refreshTokenString) {
            throw new Error("No refresh token found in secure storage.");
        }
        
        const refreshToken = new CognitoRefreshToken({ RefreshToken: refreshTokenString });

        const user = new CognitoUser({
            Username: "",
            Pool: userPool, // Stelle sicher, dass dein UserPool-Objekt hier verfügbar ist
        });

        user.refreshSession(refreshToken, (err, session) => {
            if (err) {
                console.error("Error refreshing session:", err);
                return reject(err);
            }

            const newAccessToken = session.getAccessToken().getJwtToken();
            const newIdToken = session.getIdToken().getJwtToken();

            // Update tokens using secure storage
            setSecureTokens({
                accessToken: newAccessToken,
                idToken: newIdToken
            }); 

            resolve({
                accessToken: newAccessToken,
                idToken: newIdToken,
            });
        });
    });
};

export const checkAndRefreshToken = (): Promise<AuthResult> => {
    const accessToken = getSecureToken('accessToken');
    
    if(!accessToken) {
        return Promise.reject('Kein Access-Token gefunden. Benutzer muss sich anmelden')
    }
    const expirationTime = getTokenExpirationTime(accessToken);
    const currentTime = Math.floor(Date.now() / 1000);
    if(currentTime > expirationTime) {
        console.log('Access-Token abgelaufen, versuche zu erneuern');
        return refreshTokens() as Promise<AuthResult>;
        
    } else {
        console.log('Access-Token ist noch gueltig.');
        return Promise.resolve({
            accessToken,
            idToken: getSecureToken('idToken') || '',
            refreshToken: getSecureToken('refreshToken') || '',
        })
    }
}

export const getTokenExpirationTime = (token: string): number => {
    try {
        // Validate token format (must have 3 parts: header.payload.signature)
        if (!token || token.split('.').length !== 3) {
            throw new Error('Invalid JWT format');
        }

        // Use jwt-decode for secure parsing (validates structure)
        const decoded = jwtDecode<{ exp?: number }>(token);

        // Validate exp field exists and is a number
        if (decoded.exp === undefined || decoded.exp === null || typeof decoded.exp !== 'number') {
            throw new Error('Token missing expiration');
        }

        return decoded.exp;
    } catch (error) {
        // Re-throw validation errors with clear message
        if (error instanceof Error &&
            (error.message.includes('Invalid JWT format') ||
             error.message.includes('Token missing expiration'))) {
            throw error;
        }
        // Wrap other errors (malformed base64, invalid JSON, etc.)
        throw new Error(`Invalid JWT format: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
};

export const logout = () => {
    // Clear all tokens from secure storage
    clearSecureTokens();
    
    // Optional: Call Cognito User Pool to sign out globally
    const user = userPool.getCurrentUser();
    if (user) {
        user.signOut();
    }
    
    console.log("Logout erfolgreich - alle Tokens wurden sicher entfernt");
};

// Export utility functions for other components to use
export { isAuthenticated, getSecureToken } from './utils/secureTokenStorage';